/**
 * 강사 관련 API 함수
 */

import type {
  GetInstructorsParams,
  GetInstructorsResponse,
  InstructorResponseDto,
} from "@repo/types";
import apiClient from "../lib/apiClient";

/**
 * 강사 목록 조회
 *
 * 엔드포인트: GET /api/course/instructor
 * - 페이지네이션 지원
 * - 검색 지원 (이름, 직업, 소개 제목)
 * - 정렬 지원 (생성일, 수정일, 이름)
 * - 소프트 딜리트: DELETED 상태 자동 제외
 *
 * @param params - 조회 파라미터 (페이지, 정렬, 필터 등)
 * @returns 강사 목록 및 페이지네이션 메타 정보
 */
export const getInstructors = async (
  params: GetInstructorsParams
): Promise<GetInstructorsResponse> => {
  try {
    console.log("📤 강사 목록 조회 요청:", {
      url: "/course/instructor",
      params,
      timestamp: new Date().toISOString(),
    });
    
    const response = await apiClient.get<GetInstructorsResponse>("/course/instructor", {
      params,
    });
    
    console.log("✅ 강사 목록 조회 성공:", {
      dataCount: response.data?.data?.length || 0,
      total: response.data?.meta?.total || 0,
      page: response.data?.meta?.page || 1,
      timestamp: new Date().toISOString(),
    });
    
    return response.data;
  } catch (error: any) {
    // 404 에러인 경우에만 빈 데이터 반환 (API가 아직 구현되지 않음)
    if (error?.response?.status === 404) {
      console.warn("⚠️  강사 목록 API가 아직 구현되지 않았습니다 (404). 빈 데이터를 반환합니다.", {
        url: "/course/instructor",
        params,
        timestamp: new Date().toISOString(),
      });
      return {
        data: [],
        meta: {
          page: 1,
          limit: params.limit || 20,
          total: 0,
          totalPages: 0,
          itemCount: 0,
        },
      };
    }
    
    // 다른 에러는 상세 로그와 함께 throw
    console.error("❌ 강사 목록 조회 실패:", {
      error,
      message: error?.message,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      data: error?.response?.data,
      params,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * 강사 상세 조회
 *
 * 엔드포인트: GET /api/course/instructor/:id
 *
 * @param instructorId - 강사 ID
 * @returns 강사 상세 정보
 * @throws API 요청 실패 시 에러
 */
export const getInstructorDetail = async (
  instructorId: number
): Promise<InstructorResponseDto> => {
  try {
    console.log("📤 강사 상세 조회 요청:", { instructorId });

    const response = await apiClient.get<InstructorResponseDto>(
      `/course/instructor/${instructorId}`
    );

    console.log("✅ 강사 상세 조회 성공:", {
      id: response.data.id,
      name: response.data.name,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 강사 상세 조회 실패:", error);
    throw error;
  }
};

/**
 * 강사 생성 요청 DTO
 */
export interface CreateInstructorDto {
  name: string;
  bioTitle: string;
  occupation?: string | null;
  bioContent?: string | null;
}

/**
 * 강사 수정 요청 DTO
 */
export interface UpdateInstructorDto {
  name?: string;
  occupation?: string | null;
  bioTitle?: string | null;
  bioContent?: string | null;
  imageUrl?: string | null;
}

/**
 * 강사 생성
 *
 * 엔드포인트: POST /api/course/instructor
 * - 필수 필드: name, bioTitle
 * - 선택 필드: occupation, bioContent
 * - 이미지 파일이 있는 경우 FormData로 전송
 *
 * @param data - 강사 생성 데이터
 * @param imageFile - 프로필 이미지 파일 (선택)
 * @returns 생성된 강사 정보
 * @throws API 요청 실패 시 에러
 */
export const createInstructor = async (
  data: CreateInstructorDto,
  imageFile?: File
): Promise<InstructorResponseDto> => {
  try {
    console.log("📤 강사 생성 요청:", {
      data,
      hasImageFile: !!imageFile,
    });

    let response;

    if (imageFile) {
      // FormData로 데이터와 이미지 파일 함께 전송
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("bioTitle", data.bioTitle);
      if (data.occupation !== undefined) {
        formData.append("occupation", data.occupation || "");
      }
      if (data.bioContent !== undefined) {
        formData.append("bioContent", data.bioContent || "");
      }
      formData.append("image", imageFile);

      response = await apiClient.post<InstructorResponseDto>(
        "/course/instructor",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      // JSON 데이터만 전송
      response = await apiClient.post<InstructorResponseDto>(
        "/course/instructor",
        data
      );
    }

    console.log("✅ 강사 생성 성공:", {
      id: response.data.id,
      name: response.data.name,
      hasImageFile: !!imageFile,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 강사 생성 실패:", error);
    throw error;
  }
};

/**
 * 강사 수정
 *
 * 엔드포인트: PATCH /api/course/instructor/:id
 * - 부분 업데이트 지원 (전송한 필드만 업데이트)
 * - 이미지 파일이 있는 경우 FormData로 전송
 *
 * @param instructorId - 강사 ID
 * @param data - 강사 수정 데이터
 * @param imageFile - 프로필 이미지 파일 (선택)
 * @returns 수정된 강사 정보
 * @throws API 요청 실패 시 에러
 */
export const updateInstructor = async (
  instructorId: number,
  data: UpdateInstructorDto,
  imageFile?: File
): Promise<InstructorResponseDto> => {
  try {
    console.log("📤 강사 수정 요청:", {
      instructorId,
      data,
      hasImageFile: !!imageFile,
    });

    let response;

    if (imageFile) {
      // FormData로 데이터와 이미지 파일 함께 전송
      const formData = new FormData();
      if (data.name) formData.append("name", data.name);
      if (data.occupation !== undefined) {
        formData.append("occupation", data.occupation || "");
      }
      if (data.bioTitle !== undefined) {
        formData.append("bioTitle", data.bioTitle || "");
      }
      if (data.bioContent !== undefined) {
        formData.append("bioContent", data.bioContent || "");
      }
      formData.append("image", imageFile);

      response = await apiClient.patch<InstructorResponseDto>(
        `/course/instructor/${instructorId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      // JSON 데이터만 전송
      response = await apiClient.patch<InstructorResponseDto>(
        `/course/instructor/${instructorId}`,
        data
      );
    }

    console.log("✅ 강사 수정 성공:", {
      id: response.data.id,
      name: response.data.name,
      hasImageFile: !!imageFile,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 강사 수정 실패:", error);
    throw error;
  }
};

/**
 * 강사 삭제
 *
 * 엔드포인트: DELETE /api/course/instructor/:id
 *
 * @param instructorId - 강사 ID
 * @throws API 요청 실패 시 에러
 */
export const deleteInstructor = async (instructorId: number): Promise<void> => {
  try {
    console.log("📤 강사 삭제 요청:", { instructorId });

    await apiClient.delete(`/course/instructor/${instructorId}`);

    console.log("✅ 강사 삭제 성공:", { instructorId });
  } catch (error) {
    console.error("❌ 강사 삭제 실패:", error);
    throw error;
  }
};
